/*
 * XQrCodesModal.tsx - ported from the preview kit's x-qr-codes-modal.tsx
 *
 * THE X LINK CHANGE ANNOUNCEMENT: shown once to an account with X
 * destinations, saying X now charges for posts with a tappable link, that
 * the URL stays in the post but can only be copied, and that a QR code in the
 * image gives followers something to tap instead. The primary turns QR codes on for all of the account's X
 * destinations; a second line points at the link page.
 *
 * DRAWN: `S311lGXE6JSyTnNAXHmtR7`, frame `30113:1407` ("X QR codes — Modal
 * (shadcn)"), card `30113:1408`. It is the simple modal shell - 440px, 24
 * between blocks, centred header with the close on its row, horizontal footer
 * - so it composes `SimpleModal` rather than redrawing it. Only the body is
 * new: the example image, the scope paragraph and the link page line, 12px
 * apart.
 *
 * Rendering it opens it: SimpleModal is the open Dialog, portal and
 * `.tw-root` patch included. Unmount it to close.
 *
 * OPEN BEFORE WIRING (Emily): commit 29c6dab95 on feature/twitterQR turns
 * QR codes on by default for every X destination that never saved the
 * setting, which today is all of them. With that shipped, "This turns QR
 * codes on" and the Turn on QR codes button do nothing, and Not now does not
 * leave them off. Every string and both labels are props so the answer is
 * copy, not a rebuild.
 *
 * WIRING: every control is inert here.
 *   - `onConfirm`: turn postQrCode on for all of the account's X
 *     destinations, drive `busy` while that is in flight, and set `error` if
 *     it fails. Closing and remembering the modal was seen is the caller's.
 *   - `onCancel` / `onClose`: record the modal as seen, so it shows once.
 *   - `linkPageHref`: the link page settings. `null` drops the line, for an
 *     account whose link page is already set up.
 *   - `assetBaseUrl`: `assetBase(app.config)` from `@/lib/asset-base`. The
 *     image is ui/src/img/new-features/x-qr-example.png, built to dist/img.
 */

import * as React from "react"

import SimpleModal from "@/shadcn/components/SimpleModal"
import { cn } from "@/lib/utils"

export type XQrCodesModalProps = {
  className?: string
  title?: string
  description?: React.ReactNode
  /** The paragraph under the image: what the QR code does and how far it reaches. */
  scope?: React.ReactNode
  /** The link page line. Only drawn when `linkPageHref` is set. */
  linkPageLead?: string
  linkPageLabel?: string
  /** `null` drops the link page line. */
  linkPageHref?: string | null
  /**
   * Takes over a plain click on the link, so the app can route to it rather
   * than reload onto it. The href stays, for opening it in a new tab.
   */
  onLinkPageClick?: () => void
  /** Where dist/img is served from: `assetBase(app.config)`. */
  assetBaseUrl?: string
  /**
   * Overrides the bundled example image. PLACEHOLDER ART in the frame (the layer says so): a screenshot of an
   * illustrated post with a QR code in its corner and X's action row under
   * it. The final asset replaces this file at 720x462 (2x the 360 it is drawn at).
   */
  imageSrc?: string
  imageAlt?: string
  confirmLabel?: string
  cancelLabel?: string | null
  /** In flight: the confirm spins, both buttons lock. */
  busy?: boolean
  busyLabel?: string
  /**
   * EXTRAPOLATED - the frame draws no failure. A line above the footer,
   * because the modal stays open for a retry.
   */
  error?: React.ReactNode
  onConfirm: () => void
  onCancel?: () => void
  onClose: () => void
}

export default function XQrCodesModal({
  className,
  title = "Links are changing on X",
  description = "X now charges for every post that contains a tappable link and then shows those posts to fewer people. We’ll continue sharing link URLs in your posts, but followers copy them rather than tap them.",
  /*
   * The frame says "in its output settings". CLAUDE.md's vocabulary rule is
   * *destinations*, never "outputs", in user-facing copy, so this reads
   * "destination settings" and the swap is flagged in HANDOFF.md for Emily.
   */
  scope = "Good news, we can also add a small QR code to your posted images, making it even easier for your X audience to reach your page. Turn QR codes on for all of your connected X accounts by clicking below.",
  linkPageLead = "Your link page gives X followers a tappable route too.",
  linkPageLabel = "Set up your Link Page",
  linkPageHref = "#",
  onLinkPageClick,
  assetBaseUrl = "/dist/img",
  imageSrc,
  imageAlt = "An X post whose image carries a QR code in its corner",
  confirmLabel = "Turn on QR codes",
  cancelLabel = "Not now",
  busy = false,
  busyLabel = "Turning on...",
  error,
  onConfirm,
  onCancel,
  onClose,
}: XQrCodesModalProps) {
  return (
    <SimpleModal
      /*
       * The tallest modal on SimpleModal: about 640px with the image, so a
       * short screen (a phone in landscape, a small laptop with the browser
       * chrome) cuts off the footer. Capped at the viewport less the 1rem
       * gutter the dialog keeps at the sides, and the card scrolls as one,
       * footer included. `dvh` so a phone's collapsing toolbar does not hide
       * the bottom. No scale step expresses a viewport calc.
       *
       * On a phone it is also this modal's length that puts the buttons below
       * the fold, so below `md` the text gets more of the width: 12px of card
       * padding rather than SimpleModal's 16, and an 8px gutter to the screen
       * edge rather than the dialog's 16. `md:p-6` in SimpleModal restores the
       * frame's 24 from `md`, and `sm:max-w-[440px]` the width.
       */
      className={cn(
        "tw:max-h-[calc(100dvh-2rem)] tw:max-w-[calc(100%-1rem)] tw:overflow-y-auto tw:px-3",
        className
      )}
      title={title}
      /*
       * Paragraphs read left-aligned here (Emily); only the title stays
       * centred. SimpleModal centres its header, so the description is set
       * left from this side rather than changing every modal on the shell.
       */
      description={<span className="tw:block tw:text-left">{description}</span>}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      busy={busy}
      busyLabel={busyLabel}
      onConfirm={onConfirm}
      onCancel={onCancel}
      onClose={onClose}
    >
      {/* The frame's Body: 12 between its three parts, where SimpleModal's
          own body rhythm is 16. */}
      <div className="tw:flex tw:w-full tw:flex-col tw:gap-3">
        {/*
         * The frame clips a 360-wide image into a 228-tall box with 16px
         * either side; at 360 wide the art (720x462 here, 1082x694 in the frame) is 231 tall, so the box
         * trims 1.5px top and bottom. Drawn here at its own height instead,
         * since trimming the art by a pixel and a half is not a design.
         * `max-w-[360px]`: no scale step is 360.
         */}
        {/* 32 either side on a phone, where the card's own padding is 12, so
            the image stays the height it was while the text around it widens;
            the frame's 16 from `md`. */}
        <div className="tw:flex tw:w-full tw:justify-center tw:px-8 tw:md:px-4">
          <img
            src={imageSrc ?? `${assetBaseUrl}/new-features/x-qr-example.png`}
            alt={imageAlt}
            width={720}
            height={462}
            className="tw:h-auto tw:w-full tw:max-w-[360px]"
          />
        </div>

        <p className="tw:text-sm tw:leading-5 tw:text-muted-foreground">
          {scope}
        </p>

        {linkPageHref ? (
          <p className="tw:text-sm tw:leading-5 tw:text-muted-foreground">
            {linkPageLead}
            <br />
            <a
              href={linkPageHref}
              onClick={(event) => {
                // Modified clicks still open the href the browser's way
                if (!onLinkPageClick || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
                  return
                }
                event.preventDefault()
                onLinkPageClick()
              }}
              className="tw:underline tw:underline-offset-2 tw:hover:text-foreground"
            >
              {linkPageLabel}
            </a>
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="tw:text-sm tw:leading-5 tw:text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    </SimpleModal>
  )
}
